import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Button,
  Skeleton,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Shield as ShieldIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Insights as InsightsIcon,
  FlashOn as FlashOnIcon,
  BubbleChart as BubbleChartIcon,
  PieChart as PieChartIcon,
  SwapVert as SwapVertIcon,
  Psychology as PsychologyIcon,
  Timeline as TimelineIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Calculate as CalculateIcon,
  Speed as SpeedIcon,
  AccountBalanceWallet as WalletIcon,
  Equalizer as EqualizerIcon,
} from '@mui/icons-material';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
} from 'recharts';
import { marketRiskUnifiedAPI } from '../services/api';

// ============================================================================
// Design tokens
// ============================================================================
const shadow = {
  card: '0 1px 3px rgba(15,23,42,0.04), 0 4px 14px rgba(15,23,42,0.03)',
  hover: '0 4px 20px rgba(15,23,42,0.08), 0 1px 4px rgba(15,23,42,0.04)',
};
const radius = { sm: 8, md: 12, lg: 16 };

const cardSx = {
  bgcolor: '#fff',
  borderRadius: `${radius.lg}px`,
  boxShadow: shadow.card,
  border: '1px solid #f1f5f9',
  transition: 'box-shadow 0.2s, transform 0.15s',
};

// ============================================================================
// SVG Gauge
// ============================================================================
const ScoreGauge = ({ score, color, size = 140 }) => {
  const sw = 9;
  const r = (size - sw) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(Math.max(score, 0), 100) / 100;
  const off = c * (1 - pct);
  return (
    <Box sx={{ position: 'relative', width: size, height: size, display: 'inline-flex' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={alpha(color, 0.08)} strokeWidth={sw} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={sw}
          strokeDasharray={c} strokeDashoffset={off}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)' }}
        />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={sw + 8}
          strokeDasharray={c} strokeDashoffset={off}
          strokeLinecap="round" opacity={0.06}
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)' }}
        />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontWeight: 800, color, fontSize: '2.4rem', lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: '"tnum"' }}>
          {Math.round(score)}
        </Typography>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 600, mt: 0.2, letterSpacing: '0.05em' }}>
          / 100
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================================================
// Constants
// ============================================================================
const ALERT_CONFIG = {
  GREEN:    { color: '#10b981', grad: 'linear-gradient(135deg,#10b981,#34d399)', bg: 'linear-gradient(145deg,#ecfdf5,#f0fdf4)', labelKey: 'marketRisk.safe',     icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
  GUARD:    { color: '#3b82f6', grad: 'linear-gradient(135deg,#3b82f6,#60a5fa)', bg: 'linear-gradient(145deg,#eff6ff,#dbeafe)', labelKey: 'marketRisk.guard',    icon: <WarningIcon sx={{ fontSize: 16 }} /> },
  YELLOW:   { color: '#f59e0b', grad: 'linear-gradient(135deg,#f59e0b,#fbbf24)', bg: 'linear-gradient(145deg,#fffbeb,#fef3c7)', labelKey: 'marketRisk.caution',  icon: <WarningIcon sx={{ fontSize: 16 }} /> },
  RED:      { color: '#ef4444', grad: 'linear-gradient(135deg,#ef4444,#f87171)', bg: 'linear-gradient(145deg,#fef2f2,#fee2e2)', labelKey: 'marketRisk.danger',   icon: <ShieldIcon sx={{ fontSize: 16 }} /> },
  CRITICAL: { color: '#991b1b', grad: 'linear-gradient(135deg,#991b1b,#dc2626)', bg: 'linear-gradient(145deg,#fef2f2,#fecaca)', labelKey: 'marketRisk.critical', icon: <ShieldIcon sx={{ fontSize: 16 }} /> },
};

const COMP = {
  correction:    { key: 'correction',    icon: <TrendingDownIcon sx={{ fontSize: 18 }} />, c: '#ef4444',  weight: 15, sources: ['correction_score', 'correction_severity'] },
  regime:        { key: 'regime',        icon: <InsightsIcon sx={{ fontSize: 18 }} />,     c: '#8b5cf6',  weight: 20, sources: ['regime_p_riskon', 'regime'] },
  sentiment:     { key: 'sentiment',     icon: <PsychologyIcon sx={{ fontSize: 18 }} />,   c: '#f59e0b',  weight: 13, sources: ['msi', 'market_state'] },
  highvol:       { key: 'highvol',       icon: <FlashOnIcon sx={{ fontSize: 18 }} />,      c: '#ec4899',  weight: 15, sources: ['highvol_score', 'highvol_regime'] },
  concentration: { key: 'concentration', icon: <PieChartIcon sx={{ fontSize: 18 }} />,     c: '#06b6d4',  weight: 5,  sources: ['dom5', 'breadth_pct_above20'], inverted: true },
  derivatives:   { key: 'derivatives',   icon: <SwapVertIcon sx={{ fontSize: 18 }} />,     c: '#14b8a6',  weight: 10, sources: ['basis_z', 'deriv_fear'] },
  correlation:   { key: 'correlation',   icon: <BubbleChartIcon sx={{ fontSize: 18 }} />,  c: '#6366f1',  weight: 10, sources: ['avg_correlation', 'correlation_z'] },
  leader:        { key: 'leader',        icon: <TrendingUpIcon sx={{ fontSize: 18 }} />,   c: '#f97316',  weight: 12, sources: ['leader_stress', 'leader_break_ma20'] },
};

// Source field display names (i18n keys)
const SOURCE_LABELS = {
  correction_score: 'sourceCorr',
  correction_severity: 'sourceSeverity',
  regime_p_riskon: 'sourcePRiskOn',
  regime: 'sourceRegime',
  msi: 'sourceMSI',
  market_state: 'sourceState',
  highvol_score: 'sourceHVScore',
  highvol_regime: 'sourceHVRegime',
  dom5: 'sourceDom5',
  breadth_pct_above20: 'sourceBreadth',
  basis_z: 'sourceBasisZ',
  deriv_fear: 'sourceDerivFear',
  avg_correlation: 'sourceAvgCorr',
  correlation_z: 'sourceCorrZ',
  leader_stress: 'sourceLeaderStress',
  leader_break_ma20: 'sourceLeaderBreak',
};

// Fields stored as 0-1 ratios that should display as %
const RATIO_FIELDS = new Set(['dom5', 'breadth_pct_above20', 'regime_p_riskon', 'leader_break_ma20']);

// Format source values as user-friendly labels instead of raw numbers
const fmtSrc = (key, val, t) => {
  if (typeof val !== 'number') return String(val);
  if (RATIO_FIELDS.has(key)) return `${(val <= 1 ? val * 100 : val).toFixed(1)}%`;
  switch (key) {
    case 'correction_score':
      return val >= 0.5 ? t('marketRisk.srcFmt.high') : val >= 0.2 ? t('marketRisk.srcFmt.moderate') : t('marketRisk.srcFmt.low');
    case 'msi':
      return val >= 70 ? t('marketRisk.srcFmt.optimistic') : val >= 40 ? t('marketRisk.srcFmt.neutral') : t('marketRisk.srcFmt.pessimistic');
    case 'highvol_score':
      return val >= 50 ? t('marketRisk.srcFmt.high') : val >= 25 ? t('marketRisk.srcFmt.moderate') : t('marketRisk.srcFmt.low');
    case 'basis_z':
      return val <= -1.5 ? t('marketRisk.srcFmt.veryNegative') : val <= -0.5 ? t('marketRisk.srcFmt.negative') : val >= 0.5 ? t('marketRisk.srcFmt.positive') : t('marketRisk.srcFmt.neutral');
    case 'deriv_fear':
      return val <= -1.5 ? t('marketRisk.srcFmt.high') : val <= -0.5 ? t('marketRisk.srcFmt.moderate') : t('marketRisk.srcFmt.low');
    case 'avg_correlation':
      return val >= 0.6 ? t('marketRisk.srcFmt.veryHigh') : val >= 0.4 ? t('marketRisk.srcFmt.high') : val >= 0.25 ? t('marketRisk.srcFmt.normal') : t('marketRisk.srcFmt.low');
    case 'correlation_z':
      return val >= 2 ? t('marketRisk.srcFmt.spike') : val >= 1 ? t('marketRisk.srcFmt.elevated') : t('marketRisk.srcFmt.normal');
    case 'leader_stress':
      return val >= 0.5 ? t('marketRisk.srcFmt.high') : val >= 0.2 ? t('marketRisk.srcFmt.moderate') : t('marketRisk.srcFmt.low');
    default:
      return Number.isInteger(val) ? String(val) : val.toFixed(2);
  }
};

const FLAGS = {
  correction_high:  'correction_high',
  regime_riskoff:   'regime_riskoff',
  sentiment_panic:  'sentiment_panic',
  highvol_danger:   'highvol_danger',
  narrow_rally:     'narrow_rally',
  deriv_stress:     'deriv_stress',
  correlation_spike:'correlation_spike',
  leader_breakdown: 'leader_breakdown',
};

const barColor = (v) => v >= 65 ? '#ef4444' : v >= 35 ? '#f59e0b' : '#10b981';

// inverted: high score = weak (e.g. market breadth)
const riskLabel = (v, t, inverted = false) => {
  if (inverted) {
    if (v >= 65) return t('marketRisk.riskWeak');
    if (v >= 35) return t('marketRisk.riskMed');
    return t('marketRisk.riskStrong');
  }
  if (v >= 65) return t('marketRisk.riskHigh');
  if (v >= 35) return t('marketRisk.riskMed');
  return t('marketRisk.riskLow');
};

// Semantic colors for market status values
const statusColor = (v) => {
  if (typeof v === 'number') return v >= 70 ? '#10b981' : v >= 40 ? '#f59e0b' : '#ef4444';
  const s = String(v).toUpperCase();
  if (['RISK_ON', 'NORMAL', 'LOW', 'STABLE', 'HEALTHY'].some(k => s.includes(k))) return '#10b981';
  if (['CAUTION', 'MEDIUM', 'ELEVATED', 'WARMING'].some(k => s.includes(k))) return '#f59e0b';
  if (['RISK_OFF', 'OVERHEAT', 'HIGH', 'DANGER', 'PANIC', 'STRESS', 'CRITICAL'].some(k => s.includes(k))) return '#ef4444';
  return '#6366f1';
};
const alertWt = (lv) => ({ GREEN: 0, GUARD: 1, YELLOW: 2, RED: 3, CRITICAL: 4 }[lv] ?? 0);

// Gradient bar helper
const GradBar = ({ value, color, height = 5, sx = {} }) => (
  <Box sx={{ position: 'relative', height, borderRadius: height, bgcolor: alpha(color, 0.08), overflow: 'hidden', ...sx }}>
    <Box
      sx={{
        position: 'absolute', left: 0, top: 0, height: '100%',
        width: `${Math.min(value, 100)}%`, borderRadius: height,
        background: `linear-gradient(90deg, ${alpha(color, 0.55)}, ${color})`,
        transition: 'width 0.7s cubic-bezier(0.4,0,0.2,1)',
      }}
    />
  </Box>
);

// ============================================================================
// Main
// ============================================================================
const MarketRiskUnified = () => {
  const { t, i18n } = useTranslation();
  const isVi = i18n.language === 'vi';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestData, setLatestData] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [alertsData, setAlertsData] = useState([]);
  const [vnindexCorrelation, setVnindexCorrelation] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', color: '#10b981' });

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [lr, hr, ar, cr] = await Promise.all([
        marketRiskUnifiedAPI.getLatest(),
        marketRiskUnifiedAPI.getHistory(60),
        marketRiskUnifiedAPI.getAlerts(90),
        marketRiskUnifiedAPI.getVnindexCorrelation(90),
      ]);
      setLatestData(lr.data);
      setHistoryData([...(hr.data || [])].reverse());
      setAlertsData(ar.data || []);
      setVnindexCorrelation(cr.data || null);
    } catch (e) { setError(e.message || t('marketRisk.failed')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      setSnack({ open: true, msg: isVi ? 'Đã làm mới dữ liệu' : 'Data refreshed', color: '#10b981' });
    } catch {
      setSnack({ open: true, msg: isVi ? 'Lỗi làm mới' : 'Refresh failed', color: '#ef4444' });
    } finally { setRefreshing(false); }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      await marketRiskUnifiedAPI.calculate();
      await fetchData();
      setSnack({ open: true, msg: isVi ? 'Tính toán hoàn tất' : 'Calculation complete', color: '#10b981' });
    } catch (e) {
      console.error('Calculate failed:', e);
      setSnack({ open: true, msg: isVi ? 'Lỗi tính toán' : 'Calculation failed', color: '#ef4444' });
    } finally {
      setCalculating(false);
    }
  };

  /* Loading */
  if (loading) return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header skeleton */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: 2 }} />
          <Box>
            <Skeleton width={180} height={24} sx={{ mb: 0.5 }} />
            <Skeleton width={140} height={16} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: '10px' }} />
          <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: '10px' }} />
        </Box>
      </Box>

      {/* Hero: Score + Advisory */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px 1fr' }, gap: 2, mb: 2 }}>
        {/* Score card skeleton */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Skeleton variant="circular" width={110} height={110} />
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, width: '100%' }}>
            <Skeleton width={60} height={16} />
            <Skeleton variant="rounded" width={80} height={26} sx={{ borderRadius: 2 }} />
            <Skeleton width={90} height={12} />
          </Box>
        </Box>

        {/* Advisory card skeleton */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
          {/* Advisory text */}
          <Box sx={{ py: 1.2, px: 1.5, borderBottom: '1px solid #f1f5f9', borderLeft: '3px solid #cbd5e1' }}>
            <Skeleton width="100%" height={16} />
            <Skeleton width="85%" height={16} sx={{ mt: 0.3 }} />
          </Box>
          {/* Reasons */}
          <Box sx={{ py: 1, px: 1.5, borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
            <Skeleton width={80} height={12} sx={{ mb: 0.5 }} />
            {[1,2,3].map(i => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6, mb: 0.3 }}>
                <Skeleton variant="circular" width={5} height={5} sx={{ mt: '5px' }} />
                <Skeleton width="90%" height={14} />
              </Box>
            ))}
          </Box>
          {/* Pills */}
          <Box sx={{ p: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {[1,2,3,4,5].map(i => (
              <Skeleton key={i} variant="rounded" width={60 + i * 8} height={20} sx={{ borderRadius: 1 }} />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Component scores skeleton */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1.5, mb: 2 }}>
        {[1,2,3,4,5,6].map(i => (
          <Box key={i} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
              <Skeleton width={80} height={14} />
              <Skeleton variant="circular" width={16} height={16} />
            </Box>
            <Skeleton width={40} height={28} sx={{ mb: 0.3 }} />
            <Skeleton width="60%" height={12} />
          </Box>
        ))}
      </Box>

      {/* Chart skeleton */}
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Skeleton width={150} height={20} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[1,2,3].map(i => (
              <Skeleton key={i} variant="rounded" width={60} height={28} sx={{ borderRadius: 1.5 }} />
            ))}
          </Box>
        </Box>
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
      </Box>

      {/* Flags skeleton */}
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton width={120} height={20} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1 }}>
          {[1,2,3,4,5,6,7,8].map(i => (
            <Box key={i} sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                <Skeleton variant="circular" width={14} height={14} />
                <Skeleton width="60%" height={14} />
              </Box>
              <Skeleton width="40%" height={12} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Alert severity="error" action={<Button onClick={fetchData}>{t('marketRisk.retry')}</Button>}>{error}</Alert>
    </Box>
  );

  if (!latestData) return null;

  const ac = ALERT_CONFIG[latestData.alert_level] || ALERT_CONFIG.GREEN;
  const { components, flags, source, top_drivers } = latestData;

  // ── Delta score (vs previous day) ──
  const prevScore = historyData.length >= 2 ? historyData[historyData.length - 2]?.risk_score : null;
  const delta = prevScore != null ? latestData.risk_score - prevScore : null;

  const pills = [
    source?.regime         && { k: t('marketRisk.regime'),        v: source.regime,                   c: statusColor(source.regime) },
    source?.market_state   && { k: t('marketRisk.state'),         v: source.market_state,             c: statusColor(source.market_state) },
    source?.msi != null    && { k: 'MSI',                         v: source.msi.toFixed(1),           c: statusColor(source.msi) },
    source?.highvol_regime && { k: t('marketRisk.volatility'),    v: source.highvol_regime,           c: statusColor(source.highvol_regime) },
    source?.concentration_regime && { k: t('marketRisk.concentration'), v: source.concentration_regime, c: statusColor(source.concentration_regime) },
    source?.correction_severity  && { k: t('marketRisk.severity'),      v: source.correction_severity,  c: statusColor(source.correction_severity) },
  ].filter(Boolean);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ── HEADER ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: `${radius.md}px`,
            background: ac.grad, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha(ac.color, 0.25)}`,
          }}>
            <ShieldIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
                {t('marketRisk.title')}
              </Typography>
              {latestData.trade_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ac.color, boxShadow: `0 0 6px ${ac.color}` }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {latestData.trade_date}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
              {t('marketRisk.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleCalculate}
            disabled={calculating}
            startIcon={calculating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CalculateIcon />}
            sx={{
              textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' },
              '& .MuiButton-startIcon': { mr: 0.5 },
            }}
          >
            {calculating ? t('marketRisk.calculating') : t('marketRisk.recalculate')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRefresh}
            disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
            sx={{
              textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 },
            }}
          >
            {refreshing ? t('marketRisk.refreshing') : t('marketRisk.refreshBtn')}
          </Button>
        </Box>
      </Box>

      {/* ============================================================== */}
      {/* HERO:  Score  |  Advisory / Drivers / Status                   */}
      {/* ============================================================== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px 1fr' }, gap: 2, mb: 2 }}>
        {/* ── Score Card ── */}
        <Box sx={{ ...cardSx, background: ac.bg, borderColor: alpha(ac.color, 0.12), p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <ScoreGauge score={latestData.risk_score} color={ac.color} size={110} />
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
            {/* Delta indicator */}
            {delta != null && delta !== 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                {delta > 0
                  ? <ArrowUpIcon sx={{ fontSize: 13, color: '#ef4444' }} />
                  : <ArrowDownIcon sx={{ fontSize: 13, color: '#10b981' }} />}
                <Typography sx={{
                  fontWeight: 700, fontSize: '0.72rem', fontFeatureSettings: '"tnum"',
                  color: delta > 0 ? '#ef4444' : '#10b981',
                }}>
                  {Math.abs(delta).toFixed(1)}
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.55rem', fontWeight: 500, ml: 0.2 }}>
                  {t('marketRisk.vsYesterday')}
                </Typography>
              </Box>
            )}
            <Chip
              icon={ac.icon}
              label={t(ac.labelKey)}
              sx={{
                background: ac.grad, color: '#fff', fontWeight: 700, fontSize: '0.72rem', height: 26, px: 0.3,
                '& .MuiChip-icon': { color: '#fff' },
                boxShadow: `0 2px 8px ${alpha(ac.color, 0.3)}`,
              }}
            />
            {latestData.alert_changed && (
              <Chip size="small"
                label={`${latestData.prev_alert_level} → ${latestData.alert_level}`}
                sx={{ bgcolor: alpha(ac.color, 0.1), color: ac.color, fontWeight: 600, fontSize: '0.6rem', height: 18 }}
              />
            )}
            <Typography sx={{ color: '#94a3b8', fontSize: '0.62rem', display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <WarningIcon sx={{ fontSize: 11, color: flags.active_count > 0 ? '#f59e0b' : '#d4d4d8' }} />
              {t('marketRisk.activeFlags', { count: flags.active_count })}
            </Typography>
          </Box>
        </Box>

        {/* ── Right column: single unified card ── */}
        <Box sx={{ ...cardSx, p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Advisory */}
          <Box sx={{ py: 1.2, px: 1.5, borderBottom: '1px solid #f1f5f9', borderLeft: `3px solid ${ac.color}` }}>
            <Typography sx={{ color: '#334155', fontWeight: 500, lineHeight: 1.5, fontSize: '0.78rem' }}>
              {isVi ? latestData.action_vi : latestData.action_en}
            </Typography>
          </Box>

          {/* Top Reasons */}
          {(latestData.top_reasons_vi || latestData.top_reasons_en) && (() => {
            const reasonsText = isVi ? latestData.top_reasons_vi : (latestData.top_reasons_en || latestData.top_reasons_vi);
            const items = reasonsText.split(/[;；]/).map(s => s.trim()).filter(Boolean);
            return (
              <Box sx={{ py: 1, px: 1.5, borderBottom: '1px solid #f1f5f9', bgcolor: alpha(ac.color, 0.02) }}>
                <Typography sx={{ color: '#64748b', fontSize: '0.52rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
                  {t('marketRisk.keyReasons')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  {items.map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: alpha(ac.color, 0.5), mt: '5px', flexShrink: 0 }} />
                      <Typography sx={{ color: '#475569', fontWeight: 400, lineHeight: 1.5, fontSize: '0.72rem' }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })()}

          {/* Drivers + Status in one continuous area */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, flex: 1 }}>
            {/* Top Drivers */}
            <Box sx={{ py: 1.2, px: 1.5, borderRight: { sm: '1px solid #f1f5f9' } }}>
              <Label text={t('marketRisk.topDrivers')} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mt: 0.8 }}>
                {(top_drivers || []).map((d, i) => {
                  const sv = d.score ?? 0;
                  const bc = barColor(sv);
                  const isInverted = d.inverted ?? false;
                  return (
                    <Box key={i}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.72rem' }}>
                          <Box component="span" sx={{ color: '#b0b8c5', fontWeight: 500, fontSize: '0.62rem', mr: 0.4 }}>#{i+1}</Box>
                          {isVi ? (d.name?.vi || d.name?.en || d.name) : (d.name?.en || d.name)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <Typography sx={{
                            fontSize: '0.5rem', fontWeight: 700, color: bc,
                            bgcolor: alpha(bc, 0.08), px: 0.4, py: 0.1, borderRadius: '3px',
                            textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.3,
                          }}>
                            {riskLabel(sv, t, isInverted)}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, color: bc, fontSize: '0.75rem', fontFeatureSettings: '"tnum"' }}>
                            {Math.round(sv)}
                          </Typography>
                        </Box>
                      </Box>
                      <GradBar value={sv} color={bc} height={4} />
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Status — compact 3-col layout with color indicators */}
            <Box sx={{ p: 2 }}>
              <Label text={t('marketRisk.marketStatus')} />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1px', mt: 1, bgcolor: '#f8fafc', borderRadius: `${radius.sm}px`, overflow: 'hidden' }}>
                {pills.map((p, i) => (
                  <Box key={i} sx={{
                    py: 0.8, px: 0.75, textAlign: 'center', bgcolor: '#fff',
                    position: 'relative', overflow: 'hidden',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: alpha(p.c, 0.03) },
                  }}>
                    {/* Top color accent line */}
                    <Box sx={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: 2, borderRadius: 1, bgcolor: alpha(p.c, 0.35) }} />
                    <Typography sx={{ color: '#a0aec0', fontSize: '0.52rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.3, mt: 0.3 }}>
                      {p.k}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Box sx={{
                        width: 6, height: 6, borderRadius: '50%', bgcolor: p.c, flexShrink: 0,
                        boxShadow: `0 0 0 2px ${alpha(p.c, 0.15)}`,
                      }} />
                      <Typography sx={{ fontWeight: 700, color: p.c, fontSize: '0.78rem', letterSpacing: '-0.01em' }}>
                        {p.v}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ============================================================== */}
      {/* TRADING INSIGHTS — position sizing, momentum, percentile       */}
      {/* ============================================================== */}
      {(() => {
        // ── Position Sizing ──
        const severityOrder = ['GREEN_LOW', 'GREEN', 'GUARD', 'YELLOW', 'RED', 'CRITICAL'];
        const prevIdx = severityOrder.indexOf(latestData.prev_alert_level);
        const curIdx = severityOrder.indexOf(latestData.alert_level);
        const isImproving = latestData.prev_alert_level && prevIdx > curIdx;

        const sizingMap = {
          GREEN_LOW:{ pct: '90–100%', color: '#10b981', key: isImproving ? 'sizingGreenUp' : 'sizingGreen' },
          GREEN:    { pct: '70–90%',  color: '#10b981', key: isImproving ? 'sizingGreenUp' : 'sizingGreen' },
          GUARD:    { pct: '50–70%',  color: '#3b82f6', key: isImproving ? 'sizingGuardUp' : 'sizingGuard' },
          YELLOW:   { pct: '30–50%',  color: '#f59e0b', key: isImproving ? 'sizingYellowUp' : 'sizingYellow' },
          RED:      { pct: '10–20%',  color: '#ef4444', key: isImproving ? 'sizingRedUp' : 'sizingRed' },
          CRITICAL: { pct: '0%',      color: '#ef4444', key: 'sizingCritical' },
        };
        const sizing = sizingMap[latestData.alert_level] || sizingMap.YELLOW;

        // ── Risk Momentum (last 5 sessions) ──
        const recent = historyData.slice(-5).map(d => d.risk_score).filter(v => v != null);
        let momentum = null;
        let momDir = 'flat';
        if (recent.length >= 3) {
          momentum = recent[recent.length - 1] - recent[0];
          const streak = recent.slice(1).every((v, i) => v >= recent[i]);
          const streakDown = recent.slice(1).every((v, i) => v <= recent[i]);
          if (streak && momentum > 3) momDir = 'rising';
          else if (streakDown && momentum < -3) momDir = 'falling';
          else momDir = 'flat';
        }
        const momColor = momDir === 'rising' ? '#ef4444' : momDir === 'falling' ? '#10b981' : '#64748b';

        // ── Historical Percentile ──
        const scores = historyData.map(d => d.risk_score).filter(v => v != null);
        const percentile = scores.length > 5
          ? Math.round(scores.filter(s => s <= latestData.risk_score).length / scores.length * 100)
          : null;
        const pctColor = percentile >= 75 ? '#ef4444' : percentile >= 40 ? '#f59e0b' : '#10b981';

        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 2, mb: 2 }}>
            {/* Position sizing */}
            <Box sx={{ ...cardSx, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: `${radius.sm}px`, bgcolor: alpha(sizing.color, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <WalletIcon sx={{ fontSize: 17, color: sizing.color }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.52rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {t('marketRisk.positionSizing')}
                </Typography>
                <Typography sx={{ fontWeight: 800, color: sizing.color, fontSize: '1rem', lineHeight: 1.2, fontFeatureSettings: '"tnum"' }}>
                  {sizing.pct}
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.55rem', fontWeight: 500, lineHeight: 1.2, mt: 0.15 }}>
                  {t(`marketRisk.${sizing.key}`)}
                </Typography>
              </Box>
            </Box>

            {/* Risk Momentum */}
            <Box sx={{ ...cardSx, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: `${radius.sm}px`, bgcolor: alpha(momColor, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <SpeedIcon sx={{ fontSize: 17, color: momColor }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.52rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {t('marketRisk.riskMomentum')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  {momentum != null ? (
                    <>
                      {momDir === 'rising' && <ArrowUpIcon sx={{ fontSize: 15, color: momColor }} />}
                      {momDir === 'falling' && <ArrowDownIcon sx={{ fontSize: 15, color: momColor }} />}
                      <Typography sx={{ fontWeight: 800, color: momColor, fontSize: '1rem', lineHeight: 1.2, fontFeatureSettings: '"tnum"' }}>
                        {momentum > 0 ? '+' : ''}{momentum.toFixed(1)}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>—</Typography>
                  )}
                </Box>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.55rem', fontWeight: 500, lineHeight: 1.2, mt: 0.15 }}>
                  {t(`marketRisk.mom${momDir.charAt(0).toUpperCase() + momDir.slice(1)}`)}
                </Typography>
              </Box>
            </Box>

            {/* Historical Percentile */}
            <Box sx={{ ...cardSx, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: `${radius.sm}px`, bgcolor: alpha(pctColor, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <EqualizerIcon sx={{ fontSize: 17, color: pctColor }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.52rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {t('marketRisk.percentile')}
                </Typography>
                {percentile != null ? (
                  <>
                    <Typography sx={{ fontWeight: 800, color: pctColor, fontSize: '1rem', lineHeight: 1.2, fontFeatureSettings: '"tnum"' }}>
                      {percentile}<Box component="span" sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 500 }}>%</Box>
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.55rem', fontWeight: 500, lineHeight: 1.2, mt: 0.15 }}>
                      {t('marketRisk.percentileDesc', { pct: percentile, days: scores.length })}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>—</Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })()}

      {/* ============================================================== */}
      {/* 8-COMPONENT BREAKDOWN — single card                            */}
      {/* ============================================================== */}
      <Box sx={{ ...cardSx, p: 2.5, mb: 2.5 }}>
        <Label text={t('marketRisk.components')} sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)' }, gap: { xs: 0, sm: 0 } }}>
          {Object.entries(COMP).map(([k, cfg], idx) => {
            const v = components?.[k];
            if (v == null) return null;
            const bc = barColor(v);
            // Collect source values for this component
            const srcEntries = (cfg.sources || [])
              .map(sk => source?.[sk] != null ? { key: sk, val: source[sk] } : null)
              .filter(Boolean);
            return (
              <Box
                key={k}
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.5,
                  py: 1.1, px: 1.5,
                  borderBottom: '1px solid #f8fafc',
                  transition: 'background 0.12s',
                  '&:hover': { bgcolor: '#fafbfc' },
                }}
              >
                <Box sx={{
                  width: 28, height: 28, borderRadius: `${radius.sm}px`, flexShrink: 0, mt: 0.15,
                  bgcolor: alpha(cfg.c, 0.07), display: 'flex', alignItems: 'center', justifyContent: 'center', color: cfg.c,
                }}>
                  {cfg.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '0.76rem' }}>
                        {t(`marketRisk.comp.${cfg.key}`)}
                      </Typography>
                      <Typography sx={{
                        fontSize: '0.52rem', fontWeight: 600, color: '#b0b8c5',
                        bgcolor: '#f1f5f9', px: 0.5, py: 0.1, borderRadius: '3px',
                        fontFeatureSettings: '"tnum"',
                      }}>
                        {cfg.weight}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Typography sx={{
                        fontSize: '0.58rem', fontWeight: 700, color: bc,
                        bgcolor: alpha(bc, 0.08), px: 0.6, py: 0.1, borderRadius: '4px',
                        textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.4,
                      }}>
                        {riskLabel(v, t, cfg.inverted)}
                      </Typography>
                      <Typography sx={{ fontWeight: 800, color: bc, fontSize: '0.95rem', fontFeatureSettings: '"tnum"', minWidth: 24, textAlign: 'right' }}>
                        {Math.round(v)}
                      </Typography>
                    </Box>
                  </Box>
                  <GradBar value={v} color={bc} height={4} />
                  {/* Source data inline */}
                  {srcEntries.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.5 }}>
                      {srcEntries.map(({ key: sk, val }) => (
                        <Typography key={sk} sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 500 }}>
                          <Box component="span" sx={{ color: '#b0b8c5' }}>{t(`marketRisk.src.${SOURCE_LABELS[sk]}`)}</Box>
                          {' '}
                          <Box component="span" sx={{ color: '#64748b', fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                            {fmtSrc(sk, val, t)}
                          </Box>
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ============================================================== */}
      {/* BREADTH HEALTH                                                 */}
      {/* ============================================================== */}
      {(source?.breadth_pct_above20 != null || source?.ad_ratio != null) && (() => {
        const breadthRaw = source.breadth_pct_above20;
        const breadth = breadthRaw != null ? (breadthRaw <= 1 ? breadthRaw * 100 : breadthRaw) : null;
        const adRatio = source.ad_ratio;
        const narrow = flags?.narrow_rally === true;
        const dom5Raw = source.dom5;
        const dom5 = dom5Raw != null ? (dom5Raw <= 1 ? dom5Raw * 100 : dom5Raw) : null;
        const concScore = components?.concentration;
        const concRegime = source.concentration_regime;
        // Composite health: consider ALL breadth factors
        const issues = [
          breadth != null && breadth < 30,
          adRatio != null && adRatio < 0.7,
          narrow,
          dom5 != null && dom5 >= 50,
          concScore != null && concScore >= 65,
        ].filter(Boolean).length;
        // Health = Weak if 3+ issues, Caution if 1-2, Healthy if 0
        const healthKey = issues >= 3 ? 'breadthWeak' : issues >= 1 ? 'breadthCaution' : 'breadthHealthy';
        const bColor = issues >= 3 ? '#ef4444' : issues >= 1 ? '#f59e0b' : '#10b981';
        return (
          <Box sx={{ ...cardSx, mb: 2.5, p: 1.5 }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: alpha(bColor, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PieChartIcon sx={{ fontSize: 13, color: bColor }} />
                </Box>
                <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>
                  {t('marketRisk.breadthTitle')}
                </Typography>
                <Chip size="small" label={t(`marketRisk.${healthKey}`)}
                  sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: alpha(bColor, 0.08), color: bColor }} />
                {issues > 0 && (
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#ef4444' }}>
                    {t('marketRisk.issueCount', { count: issues })}
                  </Typography>
                )}
              </Box>
              {narrow && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.2, borderRadius: '4px', bgcolor: alpha('#ef4444', 0.05), border: `1px solid ${alpha('#ef4444', 0.1)}` }}>
                  <WarningIcon sx={{ fontSize: 11, color: '#ef4444' }} />
                  <Typography sx={{ fontSize: '0.58rem', color: '#ef4444', fontWeight: 600 }}>
                    {t('marketRisk.narrowRally')}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Metrics — compact 4-col */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1px', bgcolor: '#f1f5f9', borderRadius: `${radius.sm}px`, overflow: 'hidden' }}>
              {/* Breadth % */}
              {breadth != null && (
                <Box sx={{ bgcolor: '#fff', py: 0.7, px: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.2 }}>
                    <Typography sx={{ color: '#a0aec0', fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('marketRisk.breadthPct')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.48rem', color: '#94a3b8', fontWeight: 500 }}>
                      {breadth >= 50 ? '✓' : breadth >= 30 ? '~' : '✗'} {breadth >= 50 ? t('marketRisk.riskStrong') : breadth >= 30 ? t('marketRisk.riskMed') : t('marketRisk.riskWeak')}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 800, color: breadth >= 50 ? '#10b981' : breadth >= 30 ? '#f59e0b' : '#ef4444', fontSize: '0.95rem', lineHeight: 1, fontFeatureSettings: '"tnum"' }}>
                    {breadth.toFixed(1)}%
                  </Typography>
                  <GradBar value={breadth} color={breadth >= 50 ? '#10b981' : breadth >= 30 ? '#f59e0b' : '#ef4444'} height={2.5} sx={{ mt: 0.4 }} />
                </Box>
              )}

              {/* A/D Ratio */}
              {adRatio != null && (() => {
                const adColor = adRatio >= 1 ? '#10b981' : adRatio >= 0.7 ? '#f59e0b' : '#ef4444';
                return (
                  <Box sx={{ bgcolor: '#fff', py: 0.7, px: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.2 }}>
                      <Typography sx={{ color: '#a0aec0', fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('marketRisk.adRatio')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.48rem', color: '#94a3b8', fontWeight: 500 }}>
                        {adRatio >= 1 ? t('marketRisk.bullish') : adRatio >= 0.7 ? t('marketRisk.neutral') : t('marketRisk.bearish')}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: adColor, fontSize: '0.95rem', lineHeight: 1, fontFeatureSettings: '"tnum"' }}>
                      {adRatio.toFixed(2)}
                    </Typography>
                    <GradBar value={Math.min(adRatio * 50, 100)} color={adColor} height={2.5} sx={{ mt: 0.4 }} />
                  </Box>
                );
              })()}

              {/* DOM5 */}
              {dom5 != null && (() => {
                const dColor = dom5 >= 50 ? '#ef4444' : dom5 >= 30 ? '#f59e0b' : '#10b981';
                return (
                  <Box sx={{ bgcolor: '#fff', py: 0.7, px: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.2 }}>
                      <Typography sx={{ color: '#a0aec0', fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('marketRisk.dom5Label')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.48rem', color: '#94a3b8', fontWeight: 500 }}>
                        {dom5 >= 50 ? t('marketRisk.concentrated') : t('marketRisk.diversified')}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: dColor, fontSize: '0.95rem', lineHeight: 1, fontFeatureSettings: '"tnum"' }}>
                      {dom5.toFixed(1)}%
                    </Typography>
                    <GradBar value={dom5} color={dColor} height={2.5} sx={{ mt: 0.4 }} />
                  </Box>
                );
              })()}

              {/* Concentration score */}
              {concScore != null && (() => {
                const cColor = barColor(concScore);
                return (
                  <Box sx={{ bgcolor: '#fff', py: 0.7, px: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.2 }}>
                      <Typography sx={{ color: '#a0aec0', fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('marketRisk.concScore')}
                      </Typography>
                      {concRegime && (() => {
                        const regimeMap = { CONCENTRATED: isVi ? 'Tập trung' : 'Concentrated', DIVERSIFIED: isVi ? 'Phân tán' : 'Diversified', NORMAL: isVi ? 'Bình thường' : 'Normal' };
                        const label = regimeMap[String(concRegime).toUpperCase()] || concRegime;
                        return (
                          <Typography sx={{ fontSize: '0.48rem', fontWeight: 600, color: statusColor(concRegime) }}>
                            {label}
                          </Typography>
                        );
                      })()}
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: cColor, fontSize: '0.95rem', lineHeight: 1, fontFeatureSettings: '"tnum"' }}>
                      {Math.round(concScore)}<Box component="span" sx={{ fontSize: '0.5rem', color: '#94a3b8', fontWeight: 500 }}>/100</Box>
                    </Typography>
                    <GradBar value={concScore} color={cColor} height={2.5} sx={{ mt: 0.4 }} />
                  </Box>
                );
              })()}
            </Box>
          </Box>
        );
      })()}

      {/* ============================================================== */}
      {/* CHART                                                          */}
      {/* ============================================================== */}
      <Box sx={{ ...cardSx, p: 2.5, mb: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 28, height: 28, borderRadius: `${radius.sm}px`, bgcolor: alpha('#6366f1', 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TimelineIcon sx={{ fontSize: 16, color: '#6366f1' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>
              {t('marketRisk.riskTrend')}
            </Typography>
            <Typography sx={{ color: '#cbd5e1', fontSize: '0.7rem', fontWeight: 500 }}>{t('marketRisk.days')}</Typography>
            {vnindexCorrelation?.correlation != null && (
              <Chip
                size="small"
                label={`${t('marketRisk.vnindexCorrelation')}: ${vnindexCorrelation.correlation.toFixed(2)} (${isVi ? vnindexCorrelation.strength_label_vi : vnindexCorrelation.strength_label_en})`}
                sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: alpha('#f59e0b', 0.1), color: '#b45309' }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {[{ c: '#10b981', t: t('marketRisk.safe') }, { c: '#f59e0b', t: t('marketRisk.caution') }, { c: '#ef4444', t: t('marketRisk.danger') }, { c: '#0ea5e9', t: t('marketRisk.vnindex') }].map(({ c, t: label }, i) => (
              <Box key={`${c}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c, boxShadow: `0 0 0 2px ${alpha(c, 0.12)}` }} />
                <Typography sx={{ color: '#94a3b8', fontSize: '0.63rem', fontWeight: 500 }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={historyData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="rg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="trade_date" tickFormatter={d => d ? d.slice(5) : ''} stroke="#e2e8f0" fontSize={10} tickLine={false} axisLine={false} dy={4} />
              <YAxis yAxisId="left" domain={[0, 100]} stroke="#e2e8f0" fontSize={10} tickLine={false} axisLine={false} dx={-4} />
              {historyData.some(d => d.vnindex_close != null) && (
                <YAxis yAxisId="right" orientation="right" domain={['auto', 'auto']} stroke="#e2e8f0" fontSize={10} tickLine={false} axisLine={false} dx={4} />
              )}
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  const cfg = ALERT_CONFIG[d.alert_level] || ALERT_CONFIG.GREEN;
                  return (
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(8px)',
                      border: '1px solid #e2e8f0', borderRadius: `${radius.md}px`,
                      p: 1.5, boxShadow: '0 8px 24px rgba(15,23,42,0.1)', minWidth: 150,
                    }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 500 }}>{d.trade_date}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, color: cfg.color, fontSize: '1.1rem' }}>{d.risk_score?.toFixed(1)}</Typography>
                        <Chip size="small" label={t(cfg.labelKey)} sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: cfg.color, color: '#fff' }} />
                      </Box>
                      {d.vnindex_close != null && (
                        <Typography sx={{ color: '#0369a1', fontSize: '0.68rem', mt: 0.5, fontWeight: 600 }}>
                          {t('marketRisk.vnindex')}: {d.vnindex_close.toFixed(2)}
                          {d.vnindex_change_pct != null && ` (${d.vnindex_change_pct > 0 ? '+' : ''}${d.vnindex_change_pct.toFixed(2)}%)`}
                        </Typography>
                      )}
                      {d.top_driver_1 && (() => {
                        const driverName = isVi ? (d.top_driver_1?.vi || d.top_driver_1?.en || d.top_driver_1) : (d.top_driver_1?.en || d.top_driver_1);
                        const dv = d.top_driver_1_value ?? 0;
                        const label = riskLabel(dv, t, d.top_driver_1_inverted ?? false);
                        return <Typography sx={{ color: '#64748b', fontSize: '0.68rem', mt: 0.5 }}>{driverName}: <Box component="span" sx={{ fontWeight: 700, color: barColor(dv) }}>{label} {Math.round(dv)}</Box></Typography>;
                      })()}
                    </Box>
                  );
                }}
              />
              <ReferenceLine yAxisId="left" y={35} stroke="#10b981" strokeDasharray="8 4" strokeWidth={1} strokeOpacity={0.35} />
              <ReferenceLine yAxisId="left" y={65} stroke="#ef4444" strokeDasharray="8 4" strokeWidth={1} strokeOpacity={0.35} />
              <Area yAxisId="left" type="monotone" dataKey="risk_score" stroke="#6366f1" strokeWidth={2.5} fill="url(#rg)" dot={false}
                activeDot={{ r: 4, fill: '#6366f1', stroke: '#fff', strokeWidth: 2.5 }} />
              {historyData.some(d => d.vnindex_close != null) && (
                <Line yAxisId="right" type="monotone" dataKey="vnindex_close" stroke="#0ea5e9" strokeWidth={2} dot={false}
                  activeDot={{ r: 3, fill: '#0ea5e9', stroke: '#fff', strokeWidth: 2 }} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>


      {/* ============================================================== */}
      {/* FLAGS  +  ALERT HISTORY                                        */}
      {/* ============================================================== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        {/* Flags */}
        <Box sx={{ ...cardSx, p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: `${radius.sm}px`, bgcolor: alpha('#f59e0b', 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>
                {t('marketRisk.warningFlags')}
              </Typography>
            </Box>
            <Box sx={{
              px: 1, py: 0.25, borderRadius: `${radius.sm}px`,
              bgcolor: flags.active_count >= 3 ? alpha('#ef4444', 0.08) : flags.active_count >= 1 ? alpha('#f59e0b', 0.08) : alpha('#10b981', 0.08),
            }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.72rem',
                color: flags.active_count >= 3 ? '#ef4444' : flags.active_count >= 1 ? '#f59e0b' : '#10b981',
              }}>{flags.active_count}/8</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {Object.entries(FLAGS).map(([k, flagKey]) => {
              const on = flags?.[k] === true;
              return (
                <Box key={k} sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  py: 0.85, px: 1.5, borderRadius: `${radius.sm}px`,
                  bgcolor: on ? alpha('#ef4444', 0.03) : 'transparent',
                  transition: 'background 0.15s', '&:hover': { bgcolor: on ? alpha('#ef4444', 0.06) : '#f8fafc' },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                      bgcolor: on ? '#ef4444' : '#d4d4d8',
                      boxShadow: on ? `0 0 0 3px ${alpha('#ef4444', 0.15)}` : 'none',
                      transition: 'all 0.25s',
                    }} />
                    <Typography sx={{ fontWeight: on ? 600 : 400, color: on ? '#dc2626' : '#64748b', fontSize: '0.8rem' }}>
                      {t(`marketRisk.flags.${flagKey}`)}
                    </Typography>
                  </Box>
                  {on && (
                    <Typography sx={{
                      fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
                      color: '#ef4444', bgcolor: alpha('#ef4444', 0.08), px: 0.75, py: 0.15, borderRadius: '4px',
                    }}>{t('marketRisk.alert')}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Alert History */}
        <Box sx={{ ...cardSx, p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ width: 28, height: 28, borderRadius: `${radius.sm}px`, bgcolor: alpha('#6366f1', 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SwapVertIcon sx={{ fontSize: 16, color: '#6366f1' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>
              {t('marketRisk.alertHistory')}
            </Typography>
          </Box>

          {alertsData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <CheckCircleIcon sx={{ fontSize: 32, color: '#e2e8f0', mb: 1 }} />
              <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                {t('marketRisk.noChanges')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative', pl: 2.5 }}>
              {/* vertical line */}
              <Box sx={{ position: 'absolute', left: 7.5, top: 4, bottom: 4, width: 1.5, bgcolor: '#f1f5f9', borderRadius: 1 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {alertsData.slice(0, 8).map((a, i) => {
                  const pc = ALERT_CONFIG[a.prev_alert_level] || ALERT_CONFIG.GREEN;
                  const nc = ALERT_CONFIG[a.alert_level] || ALERT_CONFIG.GREEN;
                  const up = alertWt(a.alert_level) > alertWt(a.prev_alert_level);
                  return (
                    <Box key={i} sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        position: 'absolute', left: -20, width: 10, height: 10, borderRadius: '50%',
                        bgcolor: up ? '#ef4444' : '#10b981', border: '2.5px solid #fff',
                        boxShadow: `0 0 0 2px ${up ? alpha('#ef4444', 0.18) : alpha('#10b981', 0.18)}`, zIndex: 1,
                      }} />
                      <Box sx={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        py: 0.75, px: 1.25, borderRadius: `${radius.sm}px`,
                        bgcolor: up ? alpha('#ef4444', 0.02) : alpha('#10b981', 0.02),
                        transition: 'background 0.15s', '&:hover': { bgcolor: up ? alpha('#ef4444', 0.05) : alpha('#10b981', 0.05) },
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 500, fontFeatureSettings: '"tnum"', minWidth: 72 }}>
                            {a.trade_date}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: pc.color }} />
                            <Typography sx={{ color: '#d4d4d8', fontSize: '0.65rem', mx: 0.15 }}>→</Typography>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: nc.color }} />
                          </Box>
                          <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.72rem' }}>
                            {t(pc.labelKey)} → {t(nc.labelKey)}
                          </Typography>
                        </Box>
                        <Chip size="small" label={`${a.risk_score?.toFixed(0)} — ${t(nc.labelKey)}`}
                          sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(nc.color, 0.1), color: nc.color }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Snackbar feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: snack.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{snack.msg}</Typography>
          </Box>
        }
        ContentProps={{
          sx: { bgcolor: '#1e293b', borderRadius: `${radius.md}px`, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' },
        }}
      />
    </Box>
  );
};

// ============================================================================
const Label = ({ text, sx = {} }) => (
  <Typography sx={{ color: '#94a3b8', fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', ...sx }}>
    {text}
  </Typography>
);

export default MarketRiskUnified;